import _max from 'lodash/max'
import { MapToContext } from '@utils/objectWithDefault'
import { FulfillmentCenterDetailsModel } from '@models/fulfillmentCenter'
import { WroDraftProduct } from './product'
import { WroPackagingType } from './packagingType'
import { WroSkuType } from './skuType'
import { WroLabelPurchaseType } from './labelPurchaseType'
import { WroOriginAddress } from './originAddress'
import { WroSelectedPricingGroup } from './selectedPricingGroup'
import { WroDraftShippingConfiguration, WroDraftMultiSkuBoxConfiguration } from './shipping'

export class WroDrafts {
  constructor (data) {
    const defaultData = {
      wros: { val: [], type: Array, subType: WroDraftBase }
    }
    MapToContext(this, defaultData, data)
  }
}

export class WroDraftBase {
  constructor (data) {
    const defaultData = {
      id: { val: '', type: String },
      createdDate: { val: '', type: String },
      fulfillmentCenter: { val: null, type: FulfillmentCenterDetailsModel },
      destinationFulfillmentCenter: { val: null, type: FulfillmentCenterDetailsModel },
      purchaseOrderNumber: { val: '', type: String },
      lastUpdatedDate: { val: '', type: String },
      daysTilExpiration: { val: 30, type: Number },
      autoDistributeToggle: { val: false, type: Boolean }
    }
    MapToContext(this, defaultData, data)
  }
}

export class WroDraft extends WroDraftBase {
  constructor (data) {
    super(data)
    const defaultData = {
      userId: { val: 0, type: Number },
      currentStep: { val: '', type: String },
      furthestCompletedStep: { val: 0, type: Number },
      products: { val: [], type: Array, subType: WroDraftProduct },
      packagingType: { val: null, type: WroPackagingType },
      skuType: { val: null, type: WroSkuType },
      labelPurchaseType: { val: null, type: WroLabelPurchaseType },
      originAddress: { val: null, type: WroOriginAddress },
      shippingConfigurations: { val: [], type: Array, subType: WroDraftShippingConfiguration },
      multiSKUShippingConfigurations: { val: [], type: Array, subType: WroDraftMultiSkuBoxConfiguration },
      estimatedArrivalDate: { val: '', type: String },
      pricingGroup: { val: null, type: WroSelectedPricingGroup },
      billOfLading: {
        val: {
          url: { val: '', type: String },
          name: { val: '', type: String },
          file: { val: null, type: File }
        },
        type: Object
      },
      wroCreated: { val: false, type: Boolean },
      hasProductsChanged: { val: false, type: Boolean },
      hasBoxConfigsChanged: { val: false, type: Boolean },
      hasFCChanged: { val: false, type: Boolean },
      hasEstimatedArrivalChanged: { val: false, type: Boolean },
      hasPurchaseOrderNumberChanged: { val: false, type: Boolean },
      uroIds: { val: [], type: Array, subType: Number }
    }
    MapToContext(this, defaultData, data)
    if (this.multiSKUShippingConfigurations.length === 0) this.multiSKUShippingConfigurations.push(new WroDraftMultiSkuBoxConfiguration())
  }

  initializeShippingConfigurations (shippingConfigurations = []) {
    this.shippingConfigurations = shippingConfigurations.map(sc => new WroDraftShippingConfiguration(sc))
  }

  clearShippingConfigurationValues () {
    this.shippingConfigurations.forEach(sc => {
      sc.boxConfigurations[0].quantityPerBox = 0
      sc.boxConfigurations[0].numberOfBoxes = 0
      sc.boxConfigurations[0].length = 0
      sc.boxConfigurations[0].width = 0
      sc.boxConfigurations[0].height = 0
      sc.boxConfigurations[0].weight = {
        pounds: 0,
        ounces: 0
      }
      sc.boxConfigurations.splice(1, sc.boxConfigurations.length)
    })
  }

  addSkuConfigurationById (boxConfigId, data) {
    const boxConfiguration = this.multiSKUShippingConfigurations.find(bc => bc.id === boxConfigId)
    if (boxConfiguration) boxConfiguration.addSkuConfiguration(data)
  }

  updateSkuConfigurationById (boxConfigId, skuConfigId, data) {
    const boxConfiguration = this.multiSKUShippingConfigurations.find(bc => bc.id === boxConfigId)
    if (boxConfiguration) boxConfiguration.updateSkuConfiguration(skuConfigId, data)
  }

  removeSkuConfigurationById ({ boxConfigId, skuConfigId }) {
    const boxConfigurationIndex = this.multiSKUShippingConfigurations.findIndex(bc => bc.id === boxConfigId)
    if (boxConfigurationIndex > -1) {
      const boxConfiguration = this.multiSKUShippingConfigurations[boxConfigurationIndex]
      boxConfiguration.removeSkuConfigurationById(skuConfigId)
      if (boxConfiguration.skuConfigurations.length <= 0) this.multiSKUShippingConfigurations.splice(boxConfigurationIndex, 1)
    }
  }

  addMultiSkuBoxConfiguration (data) {
    const maxId = _max(this.multiSKUShippingConfigurations.map(bc => bc.id))
    this.multiSKUShippingConfigurations.push(new WroDraftMultiSkuBoxConfiguration({ id: maxId + 1 }))
  }

  selectProductForMultiSkuConfigurationById ({ boxConfigId, skuConfigId, product }) {
    const boxConfiguration = this.multiSKUShippingConfigurations.find(bc => bc.id === boxConfigId)
    if (boxConfiguration) boxConfiguration.selectProductForSkuConfigurationById(skuConfigId, product)
  }

  selectQuantityForMultiSkuConfigurationById ({ boxConfigId, skuConfigId, value }) {
    const boxConfiguration = this.multiSKUShippingConfigurations.find(bc => bc.id === boxConfigId)
    if (boxConfiguration) boxConfiguration.selectQuantityForSkuConfigurationById(skuConfigId, value)
  }

  selectNumberOfBoxesForMultiSkuConfigurationById ({ boxConfigId, skuConfigId, value }) {
    const boxConfiguration = this.multiSKUShippingConfigurations.find(bc => bc.id === boxConfigId)
    if (boxConfiguration) boxConfiguration.selectNumberOfBoxesForSkuConfigurationById(skuConfigId, value)
  }

  clearMultiSkuShippingConfigurationValues () {
    const maxIdVal = _max(this.multiSKUShippingConfigurations.map(bc => bc.id))
    const maxId = (maxIdVal === undefined) ? 0 : maxIdVal
    this.multiSKUShippingConfigurations = [new WroDraftMultiSkuBoxConfiguration({ id: maxId + 1 })]
  }
}
